import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import {
  createEvent,
  getEventById,
  updateEvent,
  generateSlug,
  getEligibleParentEvents,
  getChildEvents,
  createSignupOption,
  updateSignupOption,
  deleteSignupOption,
  listEventInvitees,
  addEventInviteeByEmail,
  removeEventInvitee,
} from '../../lib/api/events';
import {
  getEventNotificationSettings,
  getAdminUsers,
  upsertNotificationSetting,
  sendTestNotification
} from '../../lib/api/notifications';
import type {
  Event,
  EventWithDetails,
  EventStatus,
  EventVisibility,
  EventInvitee,
  EventSignupOption,
  SignupOptionMode,
  NotificationType,
  EventNotificationSetting,
} from '../../types/database';
import FileUpload from '../../components/FileUpload';
import CustomFieldEditor from '../../components/admin/CustomFieldEditor';
import { captureError } from '../../lib/capture-error';
import { formatLocalDateRange, formatSignupOptionSchedule, isOneDaySignupOption, normalizeTimeForDb, toTimeInputValue } from '../../lib/dates';
import {
  buildInheritFromParentPayload,
  CONTACT_INFO_ONLY_KEY,
} from '../../lib/event-application';
import './AdminEventFormPage.css';

const DEFAULT_INHERIT_FROM_PARENT: Record<string, boolean> = {
  description: true,
  schedule: true,
  contacts: true,
  banner_image_url: true,
  featured_image_url: true,
  flyer_url: true,
};

function signupOptionTimesForSave(
  startDate: string | null,
  endDate: string | null,
  startTime: string | null | undefined,
  endTime: string | null | undefined
): { start_time: string | null; end_time: string | null } {
  if (!isOneDaySignupOption(startDate, endDate)) {
    return { start_time: null, end_time: null };
  }
  return {
    start_time: normalizeTimeForDb(startTime),
    end_time: normalizeTimeForDb(endTime),
  };
}

const AdminEventFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const isEdit = id && id !== 'new';

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState<EventStatus>('draft');
  const [visibility, setVisibility] = useState<EventVisibility>('public');
  const [invitees, setInvitees] = useState<EventInvitee[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [description, setDescription] = useState('');
  const [locationCity, setLocationCity] = useState('');
  const [locationState, setLocationState] = useState('');
  const [locationCountry, setLocationCountry] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [registrationDeadline, setRegistrationDeadline] = useState('');
  const [featuredImageUrl, setFeaturedImageUrl] = useState('');
  const [bannerImageUrl, setBannerImageUrl] = useState('');
  const [flyerUrl, setFlyerUrl] = useState('');
  const [signupOptionMode, setSignupOptionMode] = useState<SignupOptionMode>('single');
  const [signupOptions, setSignupOptions] = useState<EventSignupOption[]>([]);
  const [isLegacyEvent, setIsLegacyEvent] = useState(false);
  const [legacyApplicationUrl, setLegacyApplicationUrl] = useState('');
  const [isParentEvent, setIsParentEvent] = useState(false);
  const [parentEventId, setParentEventId] = useState<string>('');
  const [displayOrder, setDisplayOrder] = useState(0);
  const [confirmationContactName, setConfirmationContactName] = useState('');
  const [confirmationContactEmail, setConfirmationContactEmail] = useState('');
  const [confirmationContactPhone, setConfirmationContactPhone] = useState('');
  const [contactInfoOnly, setContactInfoOnly] = useState(false);

  /** For child events: which fields to inherit from parent (true = use parent when child empty). */
  const [inheritFromParent, setInheritFromParent] = useState<Record<string, boolean>>({});

  // Available parent events
  const [eligibleParents, setEligibleParents] = useState<Partial<Event>[]>([]);

  // Parent event full details (when editing a child, for showing "parent's current value")
  const [parentEventDetails, setParentEventDetails] = useState<EventWithDetails | null>(null);

  // Child events (if this is a parent event)
  const [childEvents, setChildEvents] = useState<Event[]>([]);

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState<EventNotificationSetting[]>([]);
  const [adminUsers, setAdminUsers] = useState<Array<{ id: string; full_name: string; email: string }>>([]);
  const [savingNotification, setSavingNotification] = useState<string | null>(null);
  const [testingNotification, setTestingNotification] = useState<string | null>(null);

  const loadEvent = useCallback(async () => {
    if (!id) return;

    try {
      const event = await getEventById(id);
      if (!event) {
        setError('Event not found');
        return;
      }

      setTitle(event.title);
      setSlug(event.slug);
      setStatus(event.status);
      setVisibility(event.visibility === 'invite' ? 'invite' : 'public');
      setDescription(event.description || '');
      setLocationCity(event.location_city || '');
      setLocationState(event.location_state || '');
      setLocationCountry(event.location_country || '');
      setStartDate(event.start_date);
      setEndDate(event.end_date);
      setRegistrationDeadline(event.registration_deadline.split('T')[0]);
      setFeaturedImageUrl(event.featured_image_url || '');
      setBannerImageUrl(event.banner_image_url || '');
      setFlyerUrl(event.flyer_url || '');
      setSignupOptionMode(event.signup_option_mode || 'single');
      setSignupOptions(event.signup_options || []);
      setIsLegacyEvent(event.is_legacy_event || false);
      setLegacyApplicationUrl(event.legacy_application_url || '');
      setParentEventId(event.parent_event_id || '');
      setDisplayOrder(event.display_order || 0);
      setIsParentEvent(event.is_parent_event || false);
      setConfirmationContactName(event.confirmation_contact_name || '');
      setConfirmationContactEmail(event.confirmation_contact_email || '');
      setConfirmationContactPhone(event.confirmation_contact_phone || '');
      setContactInfoOnly(event.inherit_from_parent?.[CONTACT_INFO_ONLY_KEY] === true);

      const inheritFields = event.inherit_from_parent
        ? Object.fromEntries(
            Object.entries(event.inherit_from_parent).filter(([key]) => key !== CONTACT_INFO_ONLY_KEY)
          )
        : {};
      setInheritFromParent(
        event.parent_event_id
          ? { ...DEFAULT_INHERIT_FROM_PARENT, ...inheritFields }
          : { ...DEFAULT_INHERIT_FROM_PARENT }
      );

      // Load parent event details when editing a child (for inherit toggles / display)
      if (event.parent_event_id) {
        try {
          const parent = await getEventById(event.parent_event_id);
          setParentEventDetails(parent ?? null);
        } catch (err) {
          captureError(err, 'Failed to load parent event');
          setParentEventDetails(null);
        }
      } else {
        setParentEventDetails(null);
      }

      // Load child events if this event has no parent (meaning it could be a parent)
      if (!event.parent_event_id) {
        try {
          const children = await getChildEvents(event.id);
          setChildEvents(children);
        } catch (err) {
          captureError(err, 'Failed to load child events');
        }
      }

      // Load notification settings
      try {
        const settings = await getEventNotificationSettings(event.id);
        setNotificationSettings(settings);
      } catch (err) {
        captureError(err, 'Failed to load notification settings');
      }

      try {
        const rows = await listEventInvitees(event.id);
        setInvitees(rows);
      } catch (err) {
        captureError(err, 'Failed to load event invitees');
        setInvitees([]);
      }
    } catch (err) {
      setError('Failed to load event');
      captureError(err, 'Failed to load event');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      loadEvent();
    }
  }, [isEdit, loadEvent]);

  // When parent event is selected, load parent details and set default inherit for new children
  useEffect(() => {
    if (!parentEventId) {
      setParentEventDetails(null);
      return;
    }
    if (!isEdit) {
      setInheritFromParent((prev) =>
        Object.keys(prev).length === 0 ? { ...DEFAULT_INHERIT_FROM_PARENT } : prev
      );
    }
    let cancelled = false;
    getEventById(parentEventId)
      .then((parent) => {
        if (!cancelled && parent) setParentEventDetails(parent);
      })
      .catch(() => {
        if (!cancelled) setParentEventDetails(null);
      });
    return () => {
      cancelled = true;
    };
  }, [parentEventId, isEdit]);

  // Load eligible parent events
  useEffect(() => {
    async function loadParents() {
      try {
        const parents = await getEligibleParentEvents();
        setEligibleParents(parents);
      } catch (err) {
        captureError(err, 'Failed to load parent events');
      }
    }
    loadParents();
  }, []);

  // Load admin users for notification settings
  useEffect(() => {
    async function loadAdmins() {
      try {
        const admins = await getAdminUsers();
        setAdminUsers(admins);
      } catch (err) {
        captureError(err, 'Failed to load admin users');
      }
    }
    loadAdmins();
  }, []);

  function handleTitleChange(newTitle: string) {
    setTitle(newTitle);
    if (!isEdit) {
      setSlug(generateSlug(newTitle));
    }
  }

  // Signup option management state
  const [newSignupOption, setNewSignupOption] = useState({
    label: '',
    price: '',
    description: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
  });
  const [savingSignupOption, setSavingSignupOption] = useState(false);
  const [editingSignupOption, setEditingSignupOption] = useState<EventSignupOption | null>(null);

  async function handleAddSignupOption() {
    if (!newSignupOption.label.trim() || !newSignupOption.price) return;

    const times = signupOptionTimesForSave(
      newSignupOption.start_date || null,
      newSignupOption.end_date || null,
      newSignupOption.start_time,
      newSignupOption.end_time
    );
    const optionData = {
      label: newSignupOption.label.trim(),
      description: newSignupOption.description.trim() || null,
      price: parseFloat(newSignupOption.price),
      start_date: newSignupOption.start_date || null,
      end_date: newSignupOption.end_date || null,
      ...times,
      display_order: signupOptions.length,
    };

    if (isEdit && id) {
      // Edit mode: save to database immediately
      setSavingSignupOption(true);
      try {
        const opt = await createSignupOption(id, optionData);
        setSignupOptions([...signupOptions, opt]);
        setNewSignupOption({ label: '', price: '', description: '', start_date: '', end_date: '', start_time: '', end_time: '' });
        showToast('Signup option added', 'success');
      } catch (err) {
        showToast('Failed to add signup option', 'error');
        captureError(err);
      } finally {
        setSavingSignupOption(false);
      }
    } else {
      // Create mode: add to local state with temporary ID
      const tempOpt: EventSignupOption = {
        ...optionData,
        id: `temp-${Date.now()}`,
        event_id: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setSignupOptions([...signupOptions, tempOpt]);
      setNewSignupOption({ label: '', price: '', description: '', start_date: '', end_date: '', start_time: '', end_time: '' });
    }
  }

  async function handleUpdateSignupOption(opt: EventSignupOption) {
    // Temp IDs (create mode) - just update local state
    if (opt.id.startsWith('temp-')) {
      setSignupOptions(signupOptions.map((o) => (o.id === opt.id ? opt : o)));
      setEditingSignupOption(null);
      return;
    }

    // Real IDs (edit mode) - update in database
    setSavingSignupOption(true);
    try {
      const updated = await updateSignupOption(opt.id, {
        label: opt.label,
        description: opt.description,
        price: opt.price,
        start_date: opt.start_date,
        end_date: opt.end_date,
        ...signupOptionTimesForSave(opt.start_date, opt.end_date, opt.start_time, opt.end_time),
      });
      setSignupOptions(signupOptions.map((o) => (o.id === updated.id ? updated : o)));
      setEditingSignupOption(null);
      showToast('Signup option updated', 'success');
    } catch (err) {
      showToast('Failed to update signup option', 'error');
      captureError(err);
    } finally {
      setSavingSignupOption(false);
    }
  }

  async function handleDeleteSignupOption(optionId: string) {
    if (!window.confirm('Delete this signup option?')) return;

    // Temp IDs (create mode) - just remove from state
    if (optionId.startsWith('temp-')) {
      setSignupOptions(signupOptions.filter((o) => o.id !== optionId));
      return;
    }

    // Real IDs (edit mode) - delete from database
    try {
      await deleteSignupOption(optionId);
      setSignupOptions(signupOptions.filter((o) => o.id !== optionId));
      showToast('Signup option deleted', 'success');
    } catch (err) {
      showToast('Failed to delete signup option', 'error');
      captureError(err);
    }
  }

  // Notification helper functions
  const NOTIFICATION_TYPES: { type: NotificationType; label: string; description: string; isAdminNotification: boolean }[] = [
    { type: 'new_application', label: 'New Application', description: 'When someone submits a new application', isAdminNotification: true },
    { type: 'application_updated', label: 'Application Updated', description: 'When an applicant updates their application', isAdminNotification: true },
    { type: 'application_approved', label: 'Application Approved', description: 'Sent to applicant when approved', isAdminNotification: false },
    { type: 'application_rejected', label: 'Application Rejected', description: 'Sent to applicant when rejected', isAdminNotification: false },
    { type: 'deadline_reminder', label: 'Deadline Reminder', description: 'Reminder before registration deadline', isAdminNotification: true },
  ];

  function getNotificationSetting(type: NotificationType): EventNotificationSetting | undefined {
    return notificationSettings.find(s => s.notification_type === type);
  }

  async function handleNotificationToggle(type: NotificationType, enabled: boolean) {
    if (!id) return;

    setSavingNotification(type);
    try {
      const existing = getNotificationSetting(type);
      const result = await upsertNotificationSetting(
        id,
        type,
        existing?.recipient_admin_ids || [],
        enabled
      );

      setNotificationSettings(prev => {
        const filtered = prev.filter(s => s.notification_type !== type);
        return [...filtered, result];
      });
    } catch (err) {
      showToast('Failed to update notification setting', 'error');
      captureError(err, 'Failed to update notification setting');
    } finally {
      setSavingNotification(null);
    }
  }

  async function handleRecipientChange(type: NotificationType, adminId: string, checked: boolean) {
    if (!id) return;

    setSavingNotification(type);
    try {
      const existing = getNotificationSetting(type);
      const currentRecipients = existing?.recipient_admin_ids || [];
      const newRecipients = checked
        ? [...currentRecipients, adminId]
        : currentRecipients.filter(id => id !== adminId);

      const result = await upsertNotificationSetting(
        id,
        type,
        newRecipients,
        existing?.enabled ?? true
      );

      setNotificationSettings(prev => {
        const filtered = prev.filter(s => s.notification_type !== type);
        return [...filtered, result];
      });
    } catch (err) {
      showToast('Failed to update notification recipients', 'error');
      captureError(err, 'Failed to update notification recipients');
    } finally {
      setSavingNotification(null);
    }
  }

  async function handleTestNotification(type: NotificationType) {
    if (!id || !user?.email) {
      showToast('Unable to send test: no email available', 'error');
      return;
    }

    setTestingNotification(type);
    try {
      const result = await sendTestNotification(id, type, user.email);
      if (result.success) {
        showToast(`Test notification sent to ${user.email}`, 'success');
      } else {
        showToast(`Failed to send test: ${result.message}`, 'error');
      }
    } catch (err) {
      showToast('Failed to send test notification', 'error');
      captureError(err, 'Failed to send test notification');
    } finally {
      setTestingNotification(null);
    }
  }

  async function handleAddInvitee(e: React.FormEvent) {
    e.preventDefault();
    if (!isEdit || !id || !inviteEmail.trim()) return;
    setInviteBusy(true);
    try {
      const row = await addEventInviteeByEmail(id, inviteEmail.trim());
      setInvitees((prev) => {
        if (prev.some((p) => p.user_id === row.user_id)) return prev;
        return [...prev, row].sort((a, b) => a.email.localeCompare(b.email));
      });
      setInviteEmail('');
      showToast(`Added ${row.email} to allowlist`, 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add invitee';
      showToast(message, 'error');
      captureError(err, 'Add event invitee');
    } finally {
      setInviteBusy(false);
    }
  }

  async function handleRemoveInvitee(inviteeId: string) {
    if (!window.confirm('Remove this person from the invite allowlist?')) return;
    setInviteBusy(true);
    try {
      await removeEventInvitee(inviteeId);
      setInvitees((prev) => prev.filter((r) => r.id !== inviteeId));
      showToast('Invitee removed', 'success');
    } catch (err) {
      showToast('Failed to remove invitee', 'error');
      captureError(err, 'Remove event invitee');
    } finally {
      setInviteBusy(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const eventData: Partial<Event> = {
        title,
        slug,
        status,
        visibility,
        description: description || null,
        location_city: locationCity || null,
        location_state: locationState || null,
        location_country: locationCountry || null,
        start_date: startDate,
        end_date: endDate,
        registration_deadline: `${registrationDeadline}T23:59:59Z`,
        featured_image_url: featuredImageUrl || null,
        banner_image_url: bannerImageUrl || null,
        flyer_url: flyerUrl || null,
        signup_option_mode: signupOptionMode,
        is_parent_event: isParentEvent,
        is_legacy_event: isLegacyEvent,
        legacy_application_url: isLegacyEvent ? legacyApplicationUrl : null,
        parent_event_id: parentEventId || null,
        display_order: displayOrder,
        inherit_from_parent: buildInheritFromParentPayload(
          parentEventId || null,
          inheritFromParent,
          contactInfoOnly
        ),
        confirmation_contact_name: confirmationContactName?.trim() || null,
        confirmation_contact_email: confirmationContactEmail?.trim() || null,
        confirmation_contact_phone: confirmationContactPhone?.trim() || null,
      };

      if (isEdit && id) {
        await updateEvent(id, eventData);
      } else {
        eventData.created_by_admin_id = user?.id || null;
        const newEvent = await createEvent(eventData);

        // Save any pending signup options
        if (signupOptions.length > 0) {
          for (const opt of signupOptions) {
            await createSignupOption(newEvent.id, {
              label: opt.label,
              description: opt.description,
              price: opt.price,
              start_date: opt.start_date,
              end_date: opt.end_date,
              start_time: opt.start_time,
              end_time: opt.end_time,
              display_order: opt.display_order,
            });
          }
        }

        navigate(`/admin/events/${newEvent.id}`);
        return;
      }

      showToast('Event saved successfully!', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to save event: ${message}`);
      captureError(err, 'Save event error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="container my-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <div className="row">
        <div className="col-lg-10 mx-auto">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>{isEdit ? 'Edit Event' : 'Create New Event'}</h1>
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="btn btn-outline-secondary"
            >
              <i className="fas fa-arrow-left me-2"></i>
              Back to Events
            </button>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Basic Information</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-8">
                    <label className="form-label">
                      Event Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={title}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      Status <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={status}
                      onChange={(e) => setStatus(e.target.value as EventStatus)}
                      required
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      Visibility <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={visibility}
                      onChange={(e) => setVisibility(e.target.value as EventVisibility)}
                      required
                    >
                      <option value="public">Public — listed on /events</option>
                      <option value="invite">Invite only — admins + allowlist</option>
                    </select>
                    <small className="form-text text-muted">
                      Invite-only stays off the public catalog. Direct URL and the field app
                      work for allowlisted accounts (and all portal admins).
                    </small>
                  </div>

                  <div className="col-12">
                    <label className="form-label">
                      URL Slug <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">/events/</span>
                      <input
                        type="text"
                        className="form-control"
                        value={slug}
                        onChange={(e) => setSlug(e.target.value)}
                        required
                        pattern="[a-z0-9-]+"
                      />
                    </div>
                    <small className="form-text text-muted">
                      Lowercase letters, numbers, and hyphens only
                    </small>
                  </div>

                  <div className="col-12">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Brief description of the event"
                    />
                  </div>

                  {/* Legacy Event Section */}
                  <div className="col-12">
                    <div className="border-top pt-3 mt-2">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="isLegacyEvent"
                          checked={isLegacyEvent}
                          onChange={(e) => setIsLegacyEvent(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="isLegacyEvent">
                          <strong>Legacy Event</strong> - Use external application form
                        </label>
                      </div>
                      <small className="form-text text-muted d-block mt-1">
                        Check this if the event uses an external application system instead of the internal one
                      </small>
                    </div>
                  </div>

                  {isLegacyEvent && (
                    <div className="col-12">
                      <label className="form-label">
                        External Application URL <span className="text-danger">*</span>
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        value={legacyApplicationUrl}
                        onChange={(e) => setLegacyApplicationUrl(e.target.value)}
                        placeholder="https://example.com/application-form"
                        required={isLegacyEvent}
                      />
                      <small className="form-text text-muted">
                        Link to the external application or registration form
                      </small>
                    </div>
                  )}

                  {/* Multi-site Event Section */}
                  <div className="col-12">
                    <div className="border-top pt-3 mt-2">
                      <h6 className="mb-3">Multi-site Event Configuration</h6>

                      {/* Parent Event Checkbox */}
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="isParentEventCheck"
                          checked={isParentEvent}
                          onChange={(e) => {
                            setIsParentEvent(e.target.checked);
                            if (e.target.checked) {
                              setParentEventId(''); // Clear parent selection if marking as parent
                            }
                          }}
                        />
                        <label className="form-check-label" htmlFor="isParentEventCheck">
                          <strong>This is a Parent Event</strong>
                        </label>
                        <small className="form-text text-muted d-block ms-4">
                          Check this if this event will have multiple sites/locations. Other events can then be created as child sites of this parent event.
                        </small>
                      </div>

                      {/* Parent Event Dropdown - only show if not marking as parent */}
                      {!isParentEvent && (
                        <>
                          <label className="form-label">Parent Event (Multi-site)</label>
                          <select
                            className="form-select"
                            value={parentEventId}
                            onChange={(e) => setParentEventId(e.target.value)}
                          >
                            <option value="">None - Standalone Event</option>
                            {eligibleParents.map((parent) => (
                              <option key={parent.id} value={parent.id}>
                                {parent.title} ({parent.location_city || 'No location'}) - {parent.start_date}
                              </option>
                            ))}
                          </select>
                          <small className="form-text text-muted">
                            Select a parent event if this is part of a multi-site event
                          </small>
                        </>
                      )}
                    </div>
                  </div>

                  {parentEventId && (
                    <div className="col-12">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        className="form-control"
                        value={displayOrder}
                        onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
                        min="0"
                      />
                      <small className="form-text text-muted">
                        Order to display this site on the parent event page (lower numbers first)
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Invite-only allowlist (saved events only) */}
            {isEdit && visibility === 'invite' && id && (
              <div className="card mb-4 border-warning">
                <div className="card-header bg-light">
                  <h5 className="mb-0">Invite allowlist</h5>
                </div>
                <div className="card-body">
                  <p className="text-muted small mb-3">
                    Portal admins always have access. Add accounts that already exist in Auth
                    (same email they sign in with). They can open the event by direct URL and use
                    the field app when mobile is enabled — the event will not appear on /events.
                  </p>
                  <form className="row g-2 align-items-end mb-3" onSubmit={handleAddInvitee}>
                    <div className="col-md-8">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="teammate@example.com"
                        required
                        disabled={inviteBusy}
                      />
                    </div>
                    <div className="col-md-4">
                      <button
                        type="submit"
                        className="btn btn-outline-primary w-100"
                        disabled={inviteBusy || !inviteEmail.trim()}
                      >
                        {inviteBusy ? 'Working…' : 'Add to allowlist'}
                      </button>
                    </div>
                  </form>
                  {invitees.length === 0 ? (
                    <p className="text-muted mb-0 small">No invitees yet — only admins can see this event.</p>
                  ) : (
                    <ul className="list-group list-group-flush">
                      {invitees.map((row) => (
                        <li
                          key={row.id}
                          className="list-group-item d-flex justify-content-between align-items-center px-0"
                        >
                          <span>{row.email}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            disabled={inviteBusy}
                            onClick={() => handleRemoveInvitee(row.id)}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {/* Inherit from parent (child events only) */}
            {parentEventId && (
              <div className="card mb-4 border-info">
                <div className="card-header bg-light">
                  <h5 className="mb-0">
                    <i className="fas fa-layer-group me-2"></i>
                    Inherit from parent
                  </h5>
                </div>
                <div className="card-body">
                  <p className="text-muted mb-3">
                    When a toggle is on, this site will show the parent&apos;s value on the public page when this site&apos;s value is empty.
                  </p>
                  <div className="row g-2">
                    {[
                      { key: 'description', label: 'Description', hasValue: parentEventDetails?.description },
                      { key: 'schedule', label: 'Schedule', hasValue: (parentEventDetails?.schedule_items?.length ?? 0) > 0 },
                      { key: 'contacts', label: 'Contacts', hasValue: (parentEventDetails?.contacts?.length ?? 0) > 0 },
                      { key: 'banner_image_url', label: 'Banner image', hasValue: parentEventDetails?.banner_image_url },
                      { key: 'featured_image_url', label: 'Featured image', hasValue: parentEventDetails?.featured_image_url },
                      { key: 'flyer_url', label: 'Flyer', hasValue: parentEventDetails?.flyer_url },
                    ].map(({ key, label, hasValue }) => (
                      <div key={key} className="col-12 col-md-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`inherit-${key}`}
                            checked={inheritFromParent[key] !== false}
                            onChange={(e) =>
                              setInheritFromParent((prev) => ({ ...prev, [key]: e.target.checked }))
                            }
                          />
                          <label className="form-check-label" htmlFor={`inherit-${key}`}>
                            Use parent&apos;s {label}
                            {hasValue && (
                              <span className="text-muted small ms-1">(parent has value)</span>
                            )}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Child Events Section (only shown for parent events) */}
            {isEdit && childEvents.length > 0 && !parentEventId && (
              <div className="card mb-4 border-primary">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">
                    <i className="fas fa-sitemap me-2"></i>
                    Parent Event - Child Sites ({childEvents.length})
                  </h5>
                </div>
                <div className="card-body">
                  <p className="text-muted mb-3">
                    This is a parent event for a multi-site outreach. The following child events are linked to this parent:
                  </p>
                  <div className="list-group">
                    {childEvents
                      .sort((a, b) => a.display_order - b.display_order)
                      .map((child) => (
                        <div key={child.id} className="list-group-item list-group-item-action">
                          <div className="d-flex w-100 justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-1">{child.title}</h6>
                              <p className="mb-1 small text-muted">
                                <i className="fas fa-map-marker-alt me-1"></i>
                                {[child.location_city, child.location_state, child.location_country]
                                  .filter(Boolean)
                                  .join(', ') || 'No location set'}
                              </p>
                              <p className="mb-0 small">
                                <i className="fas fa-calendar me-1"></i>
                                {formatLocalDateRange(child.start_date, child.end_date)}
                                <span className="ms-3">
                                  <i className="fas fa-sort me-1"></i>
                                  Order: {child.display_order}
                                </span>
                                <span className={`badge ms-2 ${
                                  child.status === 'published' ? 'bg-success' :
                                  child.status === 'draft' ? 'bg-secondary' : 'bg-warning'
                                }`}>
                                  {child.status}
                                </span>
                              </p>
                            </div>
                            <div>
                              <Link
                                to={`/admin/events/${child.id}`}
                                className="btn btn-sm btn-outline-primary"
                              >
                                <i className="fas fa-edit me-1"></i>
                                Edit
                              </Link>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                  <div className="alert alert-info mt-3 mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>Tip:</strong> To add more child sites, create a new event and select this event as its parent.
                  </div>
                </div>
              </div>
            )}

            {/* Location & Dates */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Location & Dates</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      className="form-control"
                      value={locationCity}
                      onChange={(e) => setLocationCity(e.target.value)}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">State/Province</label>
                    <input
                      type="text"
                      className="form-control"
                      value={locationState}
                      onChange={(e) => setLocationState(e.target.value)}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Country</label>
                    <input
                      type="text"
                      className="form-control"
                      value={locationCountry}
                      onChange={(e) => setLocationCountry(e.target.value)}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      Start Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      End Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      Registration Deadline <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={registrationDeadline}
                      onChange={(e) => setRegistrationDeadline(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Media & Files */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Media & Files</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12">
                    <FileUpload
                      bucket="event-images"
                      currentUrl={featuredImageUrl}
                      onUploadComplete={setFeaturedImageUrl}
                      label="Featured Image"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      helpText="Used in event cards and listings (max 5MB)"
                      showPreview
                    />
                  </div>

                  <div className="col-12">
                    <FileUpload
                      bucket="event-images"
                      currentUrl={bannerImageUrl}
                      onUploadComplete={setBannerImageUrl}
                      label="Banner Image"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      helpText="Used at the top of event detail pages (max 5MB)"
                      showPreview
                    />
                  </div>

                  <div className="col-12">
                    <FileUpload
                      bucket="event-flyers"
                      currentUrl={flyerUrl}
                      onUploadComplete={setFlyerUrl}
                      label="Event Flyer"
                      accept="application/pdf,image/png,image/jpeg,image/jpg"
                      helpText="PDF or image flyer for download (max 10MB)"
                      showPreview
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Application settings */}
            {!isParentEvent && (
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Application Form</h5>
                </div>
                <div className="card-body">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="contactInfoOnly"
                      checked={contactInfoOnly}
                      onChange={(e) => setContactInfoOnly(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="contactInfoOnly">
                      Minimal registration
                    </label>
                  </div>
                  <p className="text-muted small mb-0 mt-2">
                    When enabled, applicants use a short registration form (name, email, phone, church name).
                    They may complete their full profile afterward. Turn off for events that require the full mission application.
                  </p>
                </div>
              </div>
            )}

            {/* Signup Options */}
            {!isParentEvent && (
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Registration Options</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label">Selection Mode</label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="signupModeSingle"
                          name="signupMode"
                          checked={signupOptionMode === 'single'}
                          onChange={() => setSignupOptionMode('single')}
                        />
                        <label className="form-check-label" htmlFor="signupModeSingle">
                          Single select (radio buttons)
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="signupModeMulti"
                          name="signupMode"
                          checked={signupOptionMode === 'multi'}
                          onChange={() => setSignupOptionMode('multi')}
                        />
                        <label className="form-check-label" htmlFor="signupModeMulti">
                          Multi select (checkboxes)
                        </label>
                      </div>
                    </div>
                    <small className="text-muted">
                      Single: user picks one option. Multi: user can pick multiple options.
                    </small>
                  </div>

                  <hr />

                  {/* Existing options */}
                  {signupOptions.length === 0 ? (
                    <p className="text-muted mb-3">No registration options yet.</p>
                  ) : (
                    <div className="mb-3">
                      {signupOptions
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((option) =>
                          editingSignupOption?.id === option.id ? (
                            <div key={option.id} className="border rounded p-3 mb-2 bg-light">
                              <div className="row g-2 mb-2">
                                <div className="col-md-3">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Label"
                                    value={editingSignupOption.label}
                                    onChange={(e) =>
                                      setEditingSignupOption({ ...editingSignupOption, label: e.target.value })
                                    }
                                  />
                                </div>
                                <div className="col-md-2">
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    placeholder="Price"
                                    value={editingSignupOption.price}
                                    onChange={(e) =>
                                      setEditingSignupOption({ ...editingSignupOption, price: parseFloat(e.target.value) || 0 })
                                    }
                                    step="0.01"
                                  />
                                </div>
                                <div className="col-md-3">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Description"
                                    value={editingSignupOption.description || ''}
                                    onChange={(e) =>
                                      setEditingSignupOption({ ...editingSignupOption, description: e.target.value || null })
                                    }
                                  />
                                </div>
                                <div className="col-md-2">
                                  <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={editingSignupOption.start_date || ''}
                                    onChange={(e) => {
                                      const start_date = e.target.value || null;
                                      const end_date = editingSignupOption.end_date;
                                      const oneDay = isOneDaySignupOption(start_date, end_date);
                                      setEditingSignupOption({
                                        ...editingSignupOption,
                                        start_date,
                                        start_time: oneDay ? editingSignupOption.start_time : null,
                                        end_time: oneDay ? editingSignupOption.end_time : null,
                                      });
                                    }}
                                  />
                                </div>
                                <div className="col-md-2">
                                  <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={editingSignupOption.end_date || ''}
                                    onChange={(e) => {
                                      const end_date = e.target.value || null;
                                      const start_date = editingSignupOption.start_date;
                                      const oneDay = isOneDaySignupOption(start_date, end_date);
                                      setEditingSignupOption({
                                        ...editingSignupOption,
                                        end_date,
                                        start_time: oneDay ? editingSignupOption.start_time : null,
                                        end_time: oneDay ? editingSignupOption.end_time : null,
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              {isOneDaySignupOption(
                                editingSignupOption.start_date,
                                editingSignupOption.end_date
                              ) && (
                                <div className="row g-2 mb-2">
                                  <div className="col-md-3">
                                    <label className="form-label form-label-sm mb-0 small text-muted">
                                      Start time (optional)
                                    </label>
                                    <input
                                      type="time"
                                      className="form-control form-control-sm"
                                      value={toTimeInputValue(editingSignupOption.start_time)}
                                      onChange={(e) =>
                                        setEditingSignupOption({
                                          ...editingSignupOption,
                                          start_time: e.target.value || null,
                                        })
                                      }
                                    />
                                  </div>
                                  <div className="col-md-3">
                                    <label className="form-label form-label-sm mb-0 small text-muted">
                                      End time (optional)
                                    </label>
                                    <input
                                      type="time"
                                      className="form-control form-control-sm"
                                      value={toTimeInputValue(editingSignupOption.end_time)}
                                      onChange={(e) =>
                                        setEditingSignupOption({
                                          ...editingSignupOption,
                                          end_time: e.target.value || null,
                                        })
                                      }
                                    />
                                  </div>
                                </div>
                              )}
                              <div className="d-flex gap-1">
                                <button
                                  type="button"
                                  className="btn btn-success btn-sm"
                                  onClick={() => handleUpdateSignupOption(editingSignupOption)}
                                  disabled={savingSignupOption}
                                >
                                  <i className="fas fa-check me-1" aria-hidden></i>
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => setEditingSignupOption(null)}
                                >
                                  <i className="fas fa-times me-1" aria-hidden></i>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div key={option.id} className="border rounded p-3 mb-2 d-flex justify-content-between align-items-center">
                              <div>
                                <strong>{option.label}</strong>
                                <span className="ms-2 text-primary">${Number(option.price).toFixed(2)}</span>
                                {option.description && (
                                  <span className="ms-2 text-muted">- {option.description}</span>
                                )}
                                {formatSignupOptionSchedule(option) && (
                                  <span className="ms-2 text-muted small">
                                    ({formatSignupOptionSchedule(option)})
                                  </span>
                                )}
                              </div>
                              <div className="d-flex gap-1">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => setEditingSignupOption(option)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDeleteSignupOption(option.id)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </div>
                          )
                        )}
                    </div>
                  )}

                  {/* Add new option */}
                  <div className="border rounded p-3 bg-light">
                    <h6 className="mb-2">Add New Option</h6>
                    <div className="row g-2">
                      <div className="col-md-3">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Label *"
                          value={newSignupOption.label}
                          onChange={(e) =>
                            setNewSignupOption({ ...newSignupOption, label: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-2">
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="Price *"
                          value={newSignupOption.price}
                          onChange={(e) =>
                            setNewSignupOption({ ...newSignupOption, price: e.target.value })
                          }
                          step="0.01"
                        />
                      </div>
                      <div className="col-md-3">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Description"
                          value={newSignupOption.description}
                          onChange={(e) =>
                            setNewSignupOption({ ...newSignupOption, description: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-2">
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          placeholder="Start date"
                          value={newSignupOption.start_date}
                          onChange={(e) => {
                            const start_date = e.target.value;
                            const oneDay = isOneDaySignupOption(start_date || null, newSignupOption.end_date || null);
                            setNewSignupOption({
                              ...newSignupOption,
                              start_date,
                              start_time: oneDay ? newSignupOption.start_time : '',
                              end_time: oneDay ? newSignupOption.end_time : '',
                            });
                          }}
                        />
                      </div>
                      <div className="col-md-2">
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          placeholder="End date"
                          value={newSignupOption.end_date}
                          onChange={(e) => {
                            const end_date = e.target.value;
                            const oneDay = isOneDaySignupOption(newSignupOption.start_date || null, end_date || null);
                            setNewSignupOption({
                              ...newSignupOption,
                              end_date,
                              start_time: oneDay ? newSignupOption.start_time : '',
                              end_time: oneDay ? newSignupOption.end_time : '',
                            });
                          }}
                        />
                      </div>
                    </div>
                    {isOneDaySignupOption(newSignupOption.start_date || null, newSignupOption.end_date || null) && (
                      <div className="row g-2 mt-2">
                        <div className="col-md-3">
                          <label className="form-label form-label-sm mb-0 small text-muted">
                            Start time (optional)
                          </label>
                          <input
                            type="time"
                            className="form-control form-control-sm"
                            value={newSignupOption.start_time}
                            onChange={(e) =>
                              setNewSignupOption({ ...newSignupOption, start_time: e.target.value })
                            }
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label form-label-sm mb-0 small text-muted">
                            End time (optional)
                          </label>
                          <input
                            type="time"
                            className="form-control form-control-sm"
                            value={newSignupOption.end_time}
                            onChange={(e) =>
                              setNewSignupOption({ ...newSignupOption, end_time: e.target.value })
                            }
                          />
                        </div>
                      </div>
                    )}
                    <div className="mt-2">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleAddSignupOption}
                        disabled={savingSignupOption || !newSignupOption.label.trim() || !newSignupOption.price}
                      >
                        {!savingSignupOption && <i className="fas fa-plus me-1" aria-hidden></i>}
                        {savingSignupOption ? 'Adding...' : 'Add Option'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Note for parent events */}
            {isEdit && isParentEvent && (
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Registration Options</h5>
                </div>
                <div className="card-body">
                  <p className="text-muted mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    Registration options are configured on child events, not parent events.
                    Users register for specific locations (child events).
                  </p>
                </div>
              </div>
            )}

            {/* Application confirmation contact ("Questions?" on post-submit page) */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Application confirmation contact</h5>
              </div>
              <div className="card-body">
                <p className="text-muted small mb-3">
                  Shown on the page after applicants submit (&quot;Questions?&quot;). Email is required to show the section; phone is optional.
                </p>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Contact name (optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={confirmationContactName}
                      onChange={(e) => setConfirmationContactName(e.target.value)}
                      placeholder="e.g. Jane Smith"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Contact email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={confirmationContactEmail}
                      onChange={(e) => setConfirmationContactEmail(e.target.value)}
                      placeholder="e.g. events@example.org"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Contact phone (optional)</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={confirmationContactPhone}
                      onChange={(e) => setConfirmationContactPhone(e.target.value)}
                      placeholder="e.g. (555) 123-4567"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Notification Settings - Only show for existing events */}
            {isEdit && id && (
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="fas fa-bell me-2"></i>
                    Email Notifications
                  </h5>
                </div>
                <div className="card-body">
                  <p className="text-muted mb-4">
                    Configure which email notifications are sent for this event and who receives them.
                  </p>

                  {NOTIFICATION_TYPES.map(({ type, label, description, isAdminNotification }) => {
                    const setting = getNotificationSetting(type);
                    const isEnabled = setting?.enabled ?? false;
                    const recipients = setting?.recipient_admin_ids || [];
                    const isSaving = savingNotification === type;
                    const isTesting = testingNotification === type;

                    return (
                      <div key={type} className="notification-setting mb-4 p-3 border rounded">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1">
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`notification-${type}`}
                                checked={isEnabled}
                                onChange={(e) => handleNotificationToggle(type, e.target.checked)}
                                disabled={isSaving}
                              />
                              <label className="form-check-label fw-bold" htmlFor={`notification-${type}`}>
                                {label}
                                {isSaving && <span className="ms-2">Saving…</span>}
                              </label>
                            </div>
                            <small className="text-muted d-block ms-4">{description}</small>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleTestNotification(type)}
                            disabled={!isEnabled || isTesting}
                            title="Send test notification"
                          >
                            {isTesting ? (
                              'Sending…'
                            ) : (
                              <>
                                <i className="fas fa-paper-plane me-1"></i>
                                Test
                              </>
                            )}
                          </button>
                        </div>

                        {/* Admin recipient selection - only for admin notifications */}
                        {isAdminNotification && isEnabled && (
                          <div className="ms-4 mt-3">
                            <small className="text-muted d-block mb-2">
                              Select admins to receive this notification:
                            </small>
                            {adminUsers.length === 0 ? (
                              <p className="text-muted small">No admin users found.</p>
                            ) : (
                              <div className="row">
                                {adminUsers.map(admin => (
                                  <div key={admin.id} className="col-md-6 col-lg-4">
                                    <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`${type}-${admin.id}`}
                                        checked={recipients.includes(admin.id)}
                                        onChange={(e) => handleRecipientChange(type, admin.id, e.target.checked)}
                                        disabled={isSaving}
                                      />
                                      <label className="form-check-label small" htmlFor={`${type}-${admin.id}`}>
                                        {admin.full_name || admin.email}
                                      </label>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {recipients.length === 0 && (
                              <div className="alert alert-warning mt-2 mb-0 py-2 small">
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                No recipients selected. This notification won't be sent.
                              </div>
                            )}
                          </div>
                        )}

                        {/* Info for applicant notifications */}
                        {!isAdminNotification && isEnabled && (
                          <div className="ms-4 mt-2">
                            <small className="text-muted">
                              <i className="fas fa-info-circle me-1"></i>
                              This notification is sent directly to the applicant's email address.
                            </small>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Custom Application Fields - Only show for existing events */}
            {isEdit && id && (
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="fas fa-list-check me-2"></i>
                    Custom Application Fields
                  </h5>
                </div>
                <div className="card-body">
                  <p className="text-muted mb-4">
                    Add event-specific questions to the application form. Fields marked "Save to profile"
                    will be remembered for future applications.
                  </p>
                  <CustomFieldEditor eventId={id} />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="d-flex justify-content-between mb-5">
              <button
                type="button"
                onClick={() => navigate('/admin')}
                className="btn btn-outline-secondary"
              >
                <i className="fas fa-times me-2" aria-hidden></i>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? (
                  'Saving...'
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    {isEdit ? 'Update Event' : 'Create Event'}
                  </>
                )}
              </button>
            </div>

            {isEdit && (
              <div className="alert alert-info">
                <strong>Note:</strong> After saving basic information, you can add schedules, contacts, gallery images, and custom form fields from the event management page.
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminEventFormPage;
